import React, { useState } from 'react';
import { pricingPlans, pricingConfig } from '../utils/pricingConfig';
import { usePayment } from '../hooks/usePayment';
import { useTheme } from '../contexts/ThemeContext';
import { useContent } from '../hooks/useContent';

const PricingSection = () => {
  const [isYearly, setIsYearly] = useState(false);
  const { loading, error, handlePayment, getButtonText } = usePayment();
  const { isDark } = useTheme();
  const { content: pricingContent } = useContent('pricingSection');

  const pt = pricingContent || {
    free: 'Gratuit',
    onQuote: 'Sur devis',
    popular: 'Populaire',
    monthly: 'Mensuel',
    yearly: 'Annuel',
    perYear: '/an',
    loading: 'Chargement...',
    vatNotice: 'Tous les prix sont indiqués TTC (Toutes Taxes Comprises).',
    allPlansTitle: 'Fonctionnalités incluses dans tous les plans',
    originalPricePrefix: 'Soit ',
    originalPriceSuffix: ' sans engagement annuel',
  };

  const getPlanData = (plan) => {
    return pricingContent?.plans?.[plan.id] || plan;
  };

  const getOrderedPlans = () => {
    const plans = [...pricingPlans];
    const cleverIndex = plans.findIndex(plan => plan.popular);
    const clever = plans[cleverIndex];
    const otherPlans = plans.filter((_, index) => index !== cleverIndex);
    
    return [
      otherPlans.find(p => p.id === 'free'),
      otherPlans.find(p => p.id === 'basic'),
      clever,
      otherPlans.find(p => p.id === 'premium'),
      otherPlans.find(p => p.id === 'custom')
    ].filter(Boolean);
  };

  const getPrice = (plan) => {
    if (plan.price === 0) return getPlanData(plan).period || pt.free;
    if (plan.price === null) return pt.onQuote;
    const price = isYearly ? Math.floor(plan.price * (1 - pricingConfig.yearlyDiscount / 100) * 12) : plan.price;
    return `${price}${pricingConfig.currency}`;
  };

  const getPeriod = (plan) => {
    if (plan.price === 0 || plan.price === null) return '';
    const period = getPlanData(plan).period || plan.period;
    return isYearly ? pt.perYear : `/${period}`;
  };

  const handlePlanClick = async (plan) => {
    if (plan.id === 'custom') {
      window.location.href = 'mailto:contact@quikr.it?subject=Demande plan Custom';
      return;
    }

    if (plan.stripePriceId) {
      await handlePayment(
        plan.stripePriceId,
        plan.name,
        null,
        plan.price,
        'EUR'
      );
    }
  };

  return (
    <section className={`py-20 relative overflow-hidden ${
      isDark 
        ? 'bg-dark-900' 
        : 'bg-white'
    }`}>
      <div className="absolute inset-0 opacity-5">
        <div className={`absolute top-0 left-1/4 w-96 h-96 rounded-full mix-blend-multiply filter blur-xl ${
          isDark ? 'bg-primary-500' : 'bg-cyan-200'
        }`}></div>
        <div className={`absolute bottom-0 right-1/4 w-96 h-96 rounded-full mix-blend-multiply filter blur-xl ${
          isDark ? 'bg-secondary-500' : 'bg-cyan-100'
        }`}></div>
      </div>


      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {pt.title || pricingConfig.title}
          </h2>
          <p className={`text-xl max-w-3xl mx-auto mb-8 ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {pt.subtitle || pricingConfig.subtitle}
          </p>
          
          {error && (
            <div className={`max-w-md mx-auto mb-6 p-4 rounded-lg border ${
              isDark 
                ? 'bg-red-900/20 border-red-500/50' 
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={`text-sm ${
                isDark ? 'text-red-400' : 'text-red-600'
              }`}>{error}</p>
            </div>
          )}
          <p className={`text-xs mb-8 ${
            isDark ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {pt.vatNotice}
          </p>
          
          {pricingConfig.showMonthlyYearly && (
            <div className="flex items-center justify-center mb-12">
              <span className={`mr-3 ${
                !isYearly
                  ? (isDark ? 'text-white font-semibold' : 'text-gray-900 font-semibold')
                  : (isDark ? 'text-gray-400' : 'text-gray-500')
              }`}>
                {pt.monthly}
              </span>
              <button
                onClick={() => setIsYearly(!isYearly)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                  isYearly 
                    ? 'bg-primary-600' 
                    : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                    isYearly ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`ml-3 ${
                isYearly
                  ? (isDark ? 'text-white font-semibold' : 'text-gray-900 font-semibold')
                  : (isDark ? 'text-gray-400' : 'text-gray-500')
              }`}>
                {pt.yearly}
              </span>
              {isYearly && (
                <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-md ${
                  isDark 
                    ? 'bg-primary-600 text-white'
                    : 'bg-primary-100 text-primary-700'
                }`}>
                  -{pricingConfig.yearlyDiscount}%
                </span>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 max-w-7xl mx-auto px-4">
          {getOrderedPlans().map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-4 sm:p-6 flex flex-col h-full transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                plan.popular
                  ? (isDark 
                      ? 'bg-gradient-to-b from-primary-900/50 to-dark-800 border-2 border-primary-500'
                      : 'bg-gradient-to-b from-cyan-50 to-white border-2 border-primary-500 shadow-lg')
                  : (isDark 
                      ? 'bg-dark-800 border border-dark-700'
                      : 'bg-white border border-gray-200 shadow-md')
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className={`px-4 py-2 text-sm font-semibold rounded-full shadow-lg ${
                    isDark
                      ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white'
                      : 'bg-gradient-to-r from-primary-500 to-primary-600 text-white'
                  }`}>
                    {pt.popular}
                  </span>
                </div>
              )}

              <div className="text-center mb-4 sm:mb-6">
                <h3 className={`text-base sm:text-lg font-bold mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>{plan.name}</h3>
                <p className={`mb-3 sm:mb-4 text-xs leading-tight ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>{getPlanData(plan).description || plan.description}</p>
                
                <div className="mb-4">
                  <div className={`text-2xl font-bold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {getPrice(plan)}
                  </div>
                  {plan.price > 0 && plan.price !== null && (
                    <div className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {getPeriod(plan)}
                    </div>
                  )}
                  {plan.price === null && (
                    <div className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {plan.period}
                    </div>
                  )}
                </div>

                {isYearly && plan.price > 0 && plan.price !== null && (
                  <p className={`text-xs ${
                    isDark ? 'text-primary-400' : 'text-primary-600'
                  }`}>
                    {pt.originalPricePrefix}{plan.price}{pricingConfig.currency}/{plan.period}{pt.originalPriceSuffix}
                  </p>
                )}
              </div>

              <div className="space-y-2 mb-4 sm:mb-6 flex-grow">
                {(getPlanData(plan).features || plan.features).map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <svg
                      className="w-4 h-4 text-primary-500 mt-0.5 mr-2 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className={`text-xs leading-tight ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{feature}</span>
                  </div>
                ))}

                {(getPlanData(plan).limitations || plan.limitations).map((limitation, index) => (
                  <div key={`limit-${index}`} className="flex items-start">
                    <svg
                      className="w-4 h-4 text-gray-500 mt-0.5 mr-2 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <span className={`text-xs line-through leading-tight ${
                      isDark ? 'text-gray-500' : 'text-gray-400'
                    }`}>{limitation}</span>
                  </div>
                ))}
              </div>

              <div className="mt-auto">
                <button
                  onClick={() => handlePlanClick(plan)}
                  disabled={loading}
                  className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    plan.buttonStyle === 'primary'
                      ? 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white shadow-lg hover:shadow-xl'
                      : 'bg-transparent border-2 border-primary-500 text-primary-400 hover:bg-primary-500 hover:text-white'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      {pt.loading}
                    </div>
                  ) : (
                    getButtonText(plan.id)
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 text-center">
          <div className="max-w-4xl mx-auto">
            <h3 className={`text-2xl font-bold mb-8 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {pt.allPlansTitle}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(pt.highlights || pricingConfig.features.highlight).map((feature, index) => (
                <div key={index} className="flex items-center justify-center space-x-2">
                  <svg
                    className="w-6 h-6 text-primary-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className={`font-medium ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
